/**
 * Button settings.
 *
 * The destination half is the reason this exists as its own panel. A button
 * used to be a label and a typed-in URL, which put the burden of knowing the
 * site's own paths on a volunteer and broke every internal link the moment a
 * page was renamed. Here an internal link is a *page picked from a list*,
 * stored as an id, and the path is worked out at render time.
 *
 * The presentation half is bounded choice, never CSS: each control maps to
 * one enum in `design_schemas.ButtonPresentation`, and the renderer owns what
 * each value looks like.
 */
import React from 'react';

import { cn } from '@/lib/utils';

import { displayPath } from '../slug';
import type {
  ButtonDestination,
  ButtonPresentation,
  ColorRole,
  ContentBlock,
  SitePage,
} from '../types';
import { IconPicker } from './IconPicker';

const fieldClass =
  'mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20';

const DESTINATION_KINDS: Array<[ButtonDestination['kind'], string]> = [
  ['page', 'Interne pagina'],
  ['anchor', 'Sectie op pagina'],
  ['url', 'Externe link'],
  ['email', 'E-mailadres'],
  ['phone', 'Telefoonnummer'],
];

const BUTTON_COLOR_OPTIONS: Array<[ColorRole | '', string]> = [
  ['', 'Volgt knopstijl'],
  ['primary', 'Primair'],
  ['secondary', 'Secundair'],
  ['accent', 'Accent'],
  ['dark', 'Donker'],
  ['light', 'Licht'],
];

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

interface ButtonInspectorProps {
  block: ContentBlock;
  pages: SitePage[];
  currentPageId: string | null;
  onPatch: (patch: Partial<ContentBlock>) => void;
}

export function ButtonInspector({ block, pages, currentPageId, onPatch }: ButtonInspectorProps) {
  const destination: ButtonDestination = block.destination ?? { kind: 'page' };
  const presentation: ButtonPresentation = block.button ?? {};

  const setDestination = (patch: Partial<ButtonDestination>) =>
    onPatch({ destination: { ...destination, ...patch } });
  const setPresentation = (patch: Partial<ButtonPresentation>) =>
    onPatch({ button: { ...presentation, ...patch } });

  // Anchors are offered from the sections that actually exist on the chosen
  // page, so the author picks a real target instead of guessing an id.
  const anchorPage = pages.find((page) => page.pageId === (destination.pageId ?? currentPageId));
  const anchorOptions = (anchorPage?.sections ?? [])
    .map((section, index) => ({
      value: section.anchor ?? '',
      label: section.anchor ? `#${section.anchor}` : `Sectie ${index + 1} (nog geen anker)`,
      usable: Boolean(section.anchor),
    }))
    .filter((option) => option.usable);

  return (
    <div className="space-y-5">
      <Field label="Knoptekst">
        <input
          value={block.label ?? block.text ?? ''}
          onChange={(event) => onPatch({ label: event.target.value, text: event.target.value })}
          placeholder="Bijvoorbeeld: Plan je bezoek"
          className={fieldClass}
        />
      </Field>

      <Field label="Waar gaat de knop heen?">
        <select
          value={destination.kind}
          onChange={(event) => setDestination({ kind: event.target.value as ButtonDestination['kind'] })}
          className={fieldClass}
        >
          {DESTINATION_KINDS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </Field>

      {destination.kind === 'page' || destination.kind === 'anchor' ? (
        <Field label="Pagina">
          <select
            value={destination.pageId ?? ''}
            onChange={(event) => setDestination({ pageId: event.target.value || null })}
            className={fieldClass}
          >
            <option value="">Kies een pagina…</option>
            {pages.map((page) => (
              <option key={page.pageId} value={page.pageId}>
                {page.title || 'Naamloze pagina'} — {displayPath(page.slug)}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {destination.kind === 'anchor' ? (
        <Field label="Sectie">
          {anchorOptions.length ? (
            <select
              value={destination.anchor ?? ''}
              onChange={(event) => setDestination({ anchor: event.target.value || null })}
              className={fieldClass}
            >
              <option value="">Kies een sectie…</option>
              {anchorOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <p className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-xs leading-relaxed text-slate-500">
              Deze pagina heeft nog geen secties met een anker. Geef een sectie eerst een anker
              in de sectie-instellingen.
            </p>
          )}
        </Field>
      ) : null}

      {destination.kind === 'url' ? (
        <Field
          label="Webadres"
          hint="Alleen http(s)-adressen. Een onveilig adres wordt niet als link weergegeven."
        >
          <input
            value={destination.url ?? ''}
            onChange={(event) => setDestination({ url: event.target.value || null })}
            placeholder="https://…"
            className={fieldClass}
          />
        </Field>
      ) : null}

      {destination.kind === 'email' ? (
        <Field label="E-mailadres">
          <input
            type="email"
            value={destination.email ?? ''}
            onChange={(event) => setDestination({ email: event.target.value || null })}
            placeholder="info@onzegemeente.nl"
            className={fieldClass}
          />
        </Field>
      ) : null}

      {destination.kind === 'phone' ? (
        <Field label="Telefoonnummer">
          <input
            value={destination.phone ?? ''}
            onChange={(event) => setDestination({ phone: event.target.value || null })}
            placeholder="+31 6 1234 5678"
            className={fieldClass}
          />
        </Field>
      ) : null}

      {destination.kind === 'url' ? (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={destination.newTab ?? true}
            onChange={(event) => setDestination({ newTab: event.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          Openen in een nieuw tabblad
        </label>
      ) : null}

      <div className="border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Vormgeving</h4>

        <div className="mt-3 space-y-4">
          <Segmented
            label="Stijl"
            value={presentation.style ?? 'primary'}
            options={[
              ['primary', 'Primair'],
              ['secondary', 'Secundair'],
              ['accent', 'Accent'],
              ['outline', 'Omlijnd'],
              ['ghost', 'Subtiel'],
              ['text', 'Tekstlink'],
            ]}
            onChange={(value) => setPresentation({ style: value as ButtonPresentation['style'] })}
          />

          <Field
            label="Knopkleur"
            hint="Een themakleur blijft gekoppeld aan het thema. De tekstkleur wordt automatisch leesbaar gehouden."
          >
            <select
              value={presentation.color ?? ''}
              onChange={(event) => setPresentation({
                color: (event.target.value || null) as ColorRole | null,
                customColor: null,
              })}
              className={fieldClass}
            >
              {BUTTON_COLOR_OPTIONS.map(([value, label]) => <option key={value || 'style'} value={value}>{label}</option>)}
            </select>
          </Field>

          <ExactColorField
            value={presentation.customColor}
            onChange={(customColor) => setPresentation({ color: null, customColor })}
          />

          <Segmented
            label="Grootte"
            value={presentation.size ?? 'normal'}
            options={[['small', 'Klein'], ['normal', 'Normaal'], ['large', 'Groot']]}
            onChange={(value) => setPresentation({ size: value as ButtonPresentation['size'] })}
          />

          <Segmented
            label="Breedte"
            value={presentation.width ?? 'auto'}
            options={[['auto', 'Automatisch'], ['full', 'Volledige breedte']]}
            onChange={(value) => setPresentation({ width: value as ButtonPresentation['width'] })}
          />

          <Segmented
            label="Vorm"
            value={presentation.radius ?? 'theme'}
            options={[
              ['theme', 'Van thema'],
              ['sharp', 'Recht'],
              ['subtle', 'Subtiel'],
              ['rounded', 'Rond'],
              ['pill', 'Pil'],
            ]}
            onChange={(value) =>
              setPresentation({ radius: value === 'theme' ? null : (value as ButtonPresentation['radius']) })
            }
          />

          <Segmented
            label="Schaduw"
            value={presentation.shadow ?? 'none'}
            options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['medium', 'Medium'], ['strong', 'Sterk']]}
            onChange={(value) => setPresentation({ shadow: value as ButtonPresentation['shadow'] })}
          />

          <Segmented
            label="Hover"
            value={presentation.hover ?? 'none'}
            options={[
              ['none', 'Geen'],
              ['lift', 'Optillen'],
              ['shadow', 'Schaduw'],
              ['accent', 'Accent'],
              ['scale', 'Vergroten'],
            ]}
            onChange={(value) => setPresentation({ hover: value as ButtonPresentation['hover'] })}
          />

          <IconPicker value={presentation.icon ?? 'none'} onChange={(icon) => setPresentation({ icon })} />

          {presentation.icon && presentation.icon !== 'none' ? (
            <Segmented
              label="Positie icoon"
              value={presentation.iconPosition ?? 'right'}
              options={[['left', 'Links'], ['right', 'Rechts']]}
              onChange={(value) =>
                setPresentation({ iconPosition: value as ButtonPresentation['iconPosition'] })
              }
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ExactColorField({ value, onChange }: { value?: string | null; onChange: (value: string | null) => void }) {
  const [draft, setDraft] = React.useState(value ?? '');
  React.useEffect(() => setDraft(value ?? ''), [value]);
  const pickerValue = HEX_COLOR_PATTERN.test(draft) ? draft : '#0f766e';

  return (
    <Field label="Eigen kleur" hint="Alleen een exacte hex-kleur; wis deze om de themakleur hierboven weer te gebruiken.">
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
        <input
          type="color"
          value={pickerValue}
          aria-label="Eigen knopkleur kiezen"
          onChange={(event) => { setDraft(event.target.value); onChange(event.target.value); }}
          className="h-9 w-10 cursor-pointer rounded-lg border border-slate-300 bg-white p-1"
        />
        <input
          value={draft}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            if (next === '') onChange(null);
            else if (HEX_COLOR_PATTERN.test(next)) onChange(next);
          }}
          onBlur={() => setDraft(value ?? '')}
          placeholder="#RRGGBB"
          pattern="^#[0-9a-fA-F]{6}$"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-2 font-mono text-xs outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
        />
        {value ? <button type="button" onClick={() => { setDraft(''); onChange(null); }} className="px-1 text-xs font-semibold text-slate-500 hover:text-red-600">Wis</button> : null}
      </div>
    </Field>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div role="radiogroup" aria-label={label} className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map(([optionValue, optionLabel]) => (
          <button
            key={optionValue}
            type="button"
            role="radio"
            aria-checked={value === optionValue}
            onClick={() => onChange(optionValue)}
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
              value === optionValue
                ? 'border-teal-400 bg-teal-50 text-teal-800'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            {optionLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
